import { Check, CircleCheck } from 'lucide-react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { useFrame } from '@/lib/frame'
import { useIsPhone } from '@/lib/platform'
import type { Selection } from '@/lib/selection'
import { cn } from '@/lib/utils'

const bar =
  'order-last grid size-11 shrink-0 place-items-center rounded-full text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60 android:size-12'

// "Select", then "Done": in the phone's top bar, after its "+" (order-last keeps it there when it comes back), as
// icons (a ticked circle, then a filled tick); on a tablet or computer, a button in the page wherever this is placed.
// While picking, the bar stays put on scroll, since Done is in it (data-keep-bar).
export function SelectToggle({ selection, className }: { selection: Selection; className?: string }) {
  const phone = useIsPhone()
  const { actionSlot } = useFrame()
  const { selecting } = selection
  const onClick = selecting ? selection.stop : selection.start
  const label = selecting ? 'Done' : 'Select'
  if (phone) {
    if (!actionSlot) return null
    return createPortal(
      <button
        type="button"
        aria-label={label}
        title={label}
        data-keep-bar={selecting || undefined}
        onClick={onClick}
        className={bar}
      >
        {selecting ? (
          <span className="grid size-8 place-items-center rounded-full bg-foreground text-background">
            <Check className="size-5 stroke-[2.5]" aria-hidden />
          </span>
        ) : (
          <CircleCheck className="size-6.5 stroke-[1.75]" aria-hidden />
        )}
      </button>,
      actionSlot,
    )
  }
  return (
    <Button variant="outline" size="lg" className={className} onClick={onClick}>
      {label}
    </Button>
  )
}

export type PickedAction = { label: string; onSelect: () => void; disabled?: boolean }

// What can be done with what's picked: a dark bar along the bottom, above the tabs on a phone, saying how many are
// picked. Actions that need the right to change every one of them are given disabled when that's missing.
export function PickedBar({
  label,
  count,
  noun,
  actions,
  onPickAll,
  onDone,
}: {
  label: string
  count: number
  noun: string
  actions: PickedAction[]
  onPickAll?: () => void
  onDone?: () => void
}) {
  const action =
    'h-9 rounded-full px-3.5 text-sm font-medium transition-colors pointer-coarse:h-11 outline-none hover:bg-viewer-foreground/15 focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-40'
  return (
    <div
      role="toolbar"
      aria-label={label}
      className="fixed inset-x-3 bottom-[calc(var(--tabs-clear)+0.75rem-var(--vv-below))] z-30 mx-auto flex max-w-3xl flex-wrap items-center gap-1 rounded-[1.75rem] bg-viewer p-2 text-viewer-foreground shadow-float nav:bottom-6"
    >
      <span className="px-3 text-sm font-semibold" aria-live="polite">
        {count === 0 ? `Tap ${noun}s to pick them` : `${count} picked`}
      </span>
      <span className="flex-1" />
      {actions.map((x) => (
        <button key={x.label} type="button" className={action} disabled={!count || x.disabled} onClick={x.onSelect}>
          {x.label}
        </button>
      ))}
      {onPickAll && (
        <button type="button" className={cn(action, 'text-viewer-muted')} onClick={onPickAll}>
          Pick all
        </button>
      )}
      {onDone && (
        <button
          type="button"
          // On a phone, Done is in the top bar (SelectToggle).
          className={cn(action, 'bg-viewer-foreground text-viewer hover:bg-viewer-foreground/85 max-nav:hidden')}
          onClick={onDone}
        >
          Done
        </button>
      )}
    </div>
  )
}
